// Shared hook for property creation logic - used by both mobile and desktop
import { useState, useCallback, useEffect } from 'react';
import { 
  usePostPropertyMutation,
  usePutPropertyFeesMutation,
  usePutPropertyMerchantAccountsMutation,
} from '@/store/api/admin/propertiesApi';
import { usePatchConfigurationMutation } from '@/store/api/admin/configurationsApi';
import { useAdminUser } from './useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import { getDefaultPropertyFormState, DEFAULT_PROPERTY_ERRORS } from '@/constants/admin/propertyFormDefaults';

export interface UseCreatePropertyProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const useCreateProperty = ({ visible, onClose, onSuccess }: UseCreatePropertyProps) => {
  const { showSuccess, showError } = useToast();
  const adminUser = useAdminUser();
  
  // API mutations
  const [createProperty, { isLoading: isCreating }] = usePostPropertyMutation();
  const [updateFees, { isLoading: isUpdatingFees }] = usePutPropertyFeesMutation();
  const [updateMerchantAccounts, { isLoading: isUpdatingMerchants }] = usePutPropertyMerchantAccountsMutation();
  const [patchConfiguration] = usePatchConfigurationMutation();
  
  const isLoading = isCreating || isUpdatingFees || isUpdatingMerchants;

  // Form state
  const [formData, setFormData] = useState(() => getDefaultPropertyFormState(adminUser.selectedOrganizationId));
  const [errors, setErrors] = useState(DEFAULT_PROPERTY_ERRORS);

  // Reset form when modal/panel closes
  useEffect(() => {
    if (!visible) {
      setFormData(getDefaultPropertyFormState(adminUser.selectedOrganizationId));
      setErrors(DEFAULT_PROPERTY_ERRORS);
    }
  }, [visible, adminUser.selectedOrganizationId]);

  // Helper to save Yardi configuration via the configurations API
  const saveYardiConfig = useCallback(async (
    propertyId: number,
    organizationId: number,
    yardiData: {
      yardiDomain: string;
      yardiUsername: string;
      yardiPassword: string;
      yardiServerName: string;
      yardiDatabase: string;
      yardiBpInterfaceEntity: string;
      yardiBpInterfaceLicense: string;
    }
  ) => {
    const configsToSave = [
      { key: 'YARDI_DOMAIN', value: yardiData.yardiDomain, sensitive: false },
      { key: 'YARDI_USERNAME', value: yardiData.yardiUsername, sensitive: true },
      { key: 'YARDI_PASSWORD', value: yardiData.yardiPassword, sensitive: true },
      { key: 'YARDI_SERVER_NAME', value: yardiData.yardiServerName, sensitive: true },
      { key: 'YARDI_DATABASE', value: yardiData.yardiDatabase, sensitive: true },
      { key: 'YARDI_BP_INTERFACE_ENTITY', value: yardiData.yardiBpInterfaceEntity, sensitive: false },
      { key: 'YARDI_BP_INTERFACE_LICENSE', value: yardiData.yardiBpInterfaceLicense, sensitive: true },
    ];

    // Save each configuration (call PATCH for each key)
    for (const config of configsToSave) {
      if (!config.value || !config.value.trim()) continue; // Skip empty values

      try {
        await patchConfiguration({
          organizationId,
          propertyId,
          key: config.key,
          value: config.value.trim(),
          sensitive: config.sensitive,
          type: 'string',
          description: `Yardi ${config.key.replace('YARDI_', '').toLowerCase().replace(/_/g, ' ')}`,
        }).unwrap();
      } catch (error: any) {
        console.warn(`Failed to save ${config.key}:`, error);
        // Don't throw - we want to attempt all configs even if one fails
      }
    }
  }, [patchConfiguration]);

  // Validation logic
  const validate = useCallback((): boolean => {
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      newErrors[key as keyof typeof newErrors] = '';
    });

    if (!formData.marketingName.trim()) {
      newErrors.marketingName = 'Property name is required';
    }
    
    if (!formData.externalId || !formData.externalId.trim()) {
      newErrors.externalId = 'External ID is required';
    } else if (isNaN(Number(formData.externalId))) {
      newErrors.externalId = 'External ID must be a number';
    }
    
    if (!formData.organizationId) {
      newErrors.organizationId = 'Organization ID is required';
    }
    
    if (!formData.propertyEmailAddress.trim()) {
      newErrors.propertyEmailAddress = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.propertyEmailAddress)) {
      newErrors.propertyEmailAddress = 'Invalid email address';
    }
    
    if (!formData.propertyPhoneNumber.trim()) {
      newErrors.propertyPhoneNumber = 'Phone is required';
    }
    
    if (!formData.propertyUrl.trim()) {
      newErrors.propertyUrl = 'Website URL is required';
    }
    
    if (!formData.propertyNsfFee) {
      newErrors.propertyNsfFee = 'NSF fee is required';
    }
    
    if (!formData.propertyLateDayFee) {
      newErrors.propertyLateDayFee = 'Late day fee is required';
    }
    
    if (!formData.propertyLateDailyFee) {
      newErrors.propertyLateDailyFee = 'Late daily fee is required';
    }
    
    if (!formData.propertyMaxMonthlyFee) {
      newErrors.propertyMaxMonthlyFee = 'Max monthly fee is required';
    }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  }, [formData, errors]);

  // Input change handler
  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Multi-step submission logic
  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    let newPropertyId: number | null = null;

    try {
      // STEP 1: Create basic property
      // NOTE: Backend validation schema is wrong (expects 'name' and 'domain' from Organizations)
      // but controller actually uses 'marketingName', 'externalId', 'organizationId', 'pmsType'
      // We send both to satisfy validation and controller
      const basicPropertyData: any = {
        // What the (broken) validation expects:
        name: formData.marketingName.trim(),
        domain: 'temp-domain', // Required by validation but not used
        // What the controller actually uses:
        marketingName: formData.marketingName.trim(),
        externalId: Number(formData.externalId) || undefined, // Don't send null
        organizationId: Number(formData.organizationId),
        pmsType: formData.pmsType, // PMS type selection (yardi)
      };

      const createResponse = await createProperty({ property: basicPropertyData }).unwrap();
      newPropertyId = createResponse.response.id;

      if (!newPropertyId) {
        throw new Error('Property created but no ID returned');
      }

      // STEP 2: Configure fees if provided
      const hasValidFees = 
        formData.propertyNsfFee || 
        formData.propertyLateDayFee || 
        formData.propertyLateDailyFee || 
        formData.propertyMaxMonthlyFee;

      if (hasValidFees) {
        try {
          await updateFees({
            propertyId: newPropertyId,
            fees: {
              lateFeeStartDay: formData.propertyLateDayStart ? Number(formData.propertyLateDayStart) : 5,
              nonSufficientFundsFee: {
                basis: 'flat',
                calculationBase: 'balance',
                assessmentType: 'one-time',
                value: formData.propertyNsfFee ? Number(formData.propertyNsfFee) : 0,
              },
              lateFeeInitial: {
                basis: 'flat',
                calculationBase: 'balance',
                assessmentType: 'one-time',
                value: formData.propertyLateDayFee ? Number(formData.propertyLateDayFee) : 0,
              },
              lateFeeDaily: {
                basis: 'flat',
                calculationBase: 'balance',
                assessmentType: 'daily',
                value: formData.propertyLateDailyFee ? Number(formData.propertyLateDailyFee) : 0,
              },
              lateFeeMaxMonthly: {
                basis: 'flat',
                calculationBase: 'balance',
                assessmentType: 'one-time',
                value: formData.propertyMaxMonthlyFee ? Number(formData.propertyMaxMonthlyFee) : 0,
              },
            },
          }).unwrap();
        } catch (feeError: any) {
          console.warn('Fee configuration failed:', feeError);
          // Don't fail the whole creation if fees fail
        }
      }

      // STEP 3: Configure merchant accounts if provided
      const hasMerchantAccounts = 
        formData.creditCardMerchantAccountId || 
        formData.achMerchantAccountId;

      if (hasMerchantAccounts) {
        try {
          const merchantAccounts = [];
          
          if (formData.creditCardMerchantAccountId) {
            merchantAccounts.push({
              propertyId: newPropertyId,
              merchantId: formData.creditCardMerchantAccountId.trim(),
              isPrimary: true,
            });
          }
          
          if (formData.achMerchantAccountId) {
            merchantAccounts.push({
              propertyId: newPropertyId,
              merchantId: formData.achMerchantAccountId.trim(),
              isPrimary: !formData.creditCardMerchantAccountId, // Secondary if CC exists
            });
          }
          
          await updateMerchantAccounts({
            propertyId: newPropertyId,
            merchantAccounts: merchantAccounts as unknown as Record<string, unknown>,
          }).unwrap();
        } catch (merchantError: any) {
          console.warn('Merchant account configuration failed:', merchantError);
          // Don't fail the whole creation if merchant accounts fail
        }
      }

      // STEP 4: Save Yardi configuration if Yardi PMS is selected
      if (formData.pmsType === 'yardi' && newPropertyId) {
        const hasYardiConfig = 
          formData.yardiDomain && 
          formData.yardiUsername && 
          formData.yardiPassword &&
          formData.yardiServerName &&
          formData.yardiDatabase;

        if (hasYardiConfig) {
          try {
            await saveYardiConfig(newPropertyId, Number(formData.organizationId), {
              yardiDomain: formData.yardiDomain,
              yardiUsername: formData.yardiUsername,
              yardiPassword: formData.yardiPassword,
              yardiServerName: formData.yardiServerName,
              yardiDatabase: formData.yardiDatabase,
              yardiBpInterfaceEntity: formData.yardiBpInterfaceEntity || 'Resio',
              yardiBpInterfaceLicense: formData.yardiBpInterfaceLicense,
            });
          } catch (yardiError: any) {
            console.warn('Yardi configuration failed:', yardiError);
            // Don't fail the whole creation if Yardi config fails
          }
        }
      }

      showSuccess(`Property "${formData.marketingName}" created successfully`);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create property';
      showError(errorMessage);
      
      // If property was created but configuration failed, still close the modal
      // The property exists, just without full configuration
      if (newPropertyId) {
        onSuccess?.(); // Trigger refresh
        onClose();
      }
    }
  }, [
    validate, 
    formData, 
    createProperty, 
    updateFees, 
    updateMerchantAccounts,
    saveYardiConfig,
    showSuccess, 
    showError, 
    onSuccess, 
    onClose
  ]);

  return {
    // Form state
    formData,
    errors,
    
    // Loading state
    isLoading,
    
    // Actions
    handleInputChange,
    handleSubmit,
  };
};

